/** Hooks for Graph AI extraction settings, status, jobs, and document detail. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api-client'
import type {
  DocumentGraphExtractionDetail,
  GraphExtractionJob,
  GraphExtractionProviderMode,
  GraphExtractionSettings,
} from '@agentwiki/shared'

interface CloudflareModel {
  id: string
  name: string
  contextTokens: number
}

interface SettingsResponse {
  settings: GraphExtractionSettings
  cloudflareModels: readonly CloudflareModel[]
}

export function useGraphExtractionSettings() {
  return useQuery<SettingsResponse>({
    queryKey: ['graph-extraction-settings'],
    queryFn: () => apiClient.get('/api/graph/extraction/settings'),
  })
}

export function useUpdateGraphExtractionSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<{
      enabled: boolean
      autoRunOnSave: boolean
      providerMode: GraphExtractionProviderMode
      cloudflareModel: string
      byokProviderId: string | null
      byokModel: string | null
    }>) => apiClient.put<SettingsResponse>('/api/graph/extraction/settings', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['graph-extraction-settings'] })
      qc.invalidateQueries({ queryKey: ['graph-extraction-status'] })
    },
  })
}

export function useGraphExtractionStatus() {
  return useQuery<{
    state: 'running' | 'idle'
    running: number
    succeeded: number
    failed: number
    skipped: number
    activity: GraphExtractionJob[]
  }>({
    queryKey: ['graph-extraction-status'],
    queryFn: () => apiClient.get('/api/graph/extraction/status'),
    refetchInterval: 15_000,
  })
}

export function useGraphExtractionJobs(limit = 25) {
  return useQuery<{ jobs: GraphExtractionJob[] }>({
    queryKey: ['graph-extraction-jobs', limit],
    queryFn: () => apiClient.get(`/api/graph/extraction/jobs?limit=${limit}`),
    refetchInterval: 15_000,
  })
}

export function useDocumentGraphExtraction(documentId: string | null) {
  return useQuery<DocumentGraphExtractionDetail>({
    queryKey: ['document-graph-extraction', documentId],
    queryFn: () => apiClient.get(`/api/graph/extraction/documents/${documentId}`),
    enabled: !!documentId,
    refetchInterval: 15_000,
  })
}

export function useRunDocumentGraphExtraction(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post<{ job: GraphExtractionJob }>(`/api/graph/extraction/documents/${documentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['document-graph-extraction', documentId] })
      qc.invalidateQueries({ queryKey: ['graph-extraction-status'] })
      qc.invalidateQueries({ queryKey: ['graph'] })
    },
  })
}

export function useBackfillGraphExtraction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { force?: boolean; offset?: number } = {}) =>
      apiClient.post<{ ok: boolean; enqueued: number; nextOffset: number | null; hasMore: boolean }>('/api/graph/extraction/backfill', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['graph-extraction-status'] })
      qc.invalidateQueries({ queryKey: ['graph-extraction-jobs'] })
    },
  })
}
